import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

type Props = {
  defaultValue?: any;
  reverse?: boolean;
  currValue?: number;
  min?: number;
  max?: number;
  ballColor?: string;
  getValue: (value: number) => void;
  ticks?: { value: number; label: string }[];
  fillBeforeBall?: boolean;
  unit?: string;
  showInput?: boolean;
  midpoint?: number; // Value that should appear at visual center of slider
  snapThreshold?: number; // Threshold in slider units (0-100) for snapping to ticks
  inputHeight?: string;
  noMargin?: boolean;
  ballOpacityDefault?: number;
  cursor?: 'pointer' | 'default';
  formatTooltip?: (value: number) => string;
};
const SliderSelect = (props: Props) => {
  const {
    min = 1,
    max = 100,
    defaultValue = 1,
    ballColor = 'var(--accent)',
    ticks = [],
    fillBeforeBall = false,
    unit = '',
    showInput = true,
    midpoint,
    snapThreshold = 3, // Default snap threshold of 3 slider units
    inputHeight = '1px',
    noMargin = false,
    ballOpacityDefault = 0.7,
    cursor = 'default',
    formatTooltip,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Convert between actual value and slider position (0-100)
  // When midpoint is set, we use a piecewise linear scale
  const valueToSlider = useCallback(
    (value: number): number => {
      if (midpoint === undefined) {
        return ((value - min) / (max - min)) * 100;
      }
      // Piecewise linear: min->0, midpoint->50, max->100
      if (value <= midpoint) {
        return ((value - min) / (midpoint - min)) * 50;
      } else {
        return 50 + ((value - midpoint) / (max - midpoint)) * 50;
      }
    },
    [min, max, midpoint],
  );

  const sliderToValue = useCallback(
    (slider: number): number => {
      if (midpoint === undefined) {
        return min + (slider / 100) * (max - min);
      }
      // Piecewise linear: 0->min, 50->midpoint, 100->max
      if (slider <= 50) {
        return min + (slider / 50) * (midpoint - min);
      } else {
        return midpoint + ((slider - 50) / 50) * (max - midpoint);
      }
    },
    [min, max, midpoint],
  );

  const getDefaultSliderValue = () => {
    const value = props.currValue ?? defaultValue;
    const numValue = Number(value);
    if (isNaN(numValue)) return defaultValue;
    if (props.reverse) return max - numValue;
    return numValue;
  };

  const formatDisplayValue = (value: number) => {
    if (isNaN(value)) return String(defaultValue);
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  const [actualValue, setActualValue] = useState<number>(
    getDefaultSliderValue(),
  );
  const [inputValue, setInputValue] = useState<string>(
    formatDisplayValue(getDefaultSliderValue()),
  );

  useEffect(() => {
    const newValue = getDefaultSliderValue();
    setActualValue(newValue);
    setInputValue(formatDisplayValue(newValue));
  }, [props.currValue, defaultValue]);

  // Find if slider position is near a tick mark and return the snapped value
  const getSnappedValue = useCallback(
    (sliderPos: number): number | null => {
      if (ticks.length === 0 || snapThreshold <= 0) return null;

      for (const tick of ticks) {
        const tickSliderPos = valueToSlider(tick.value);
        if (Math.abs(sliderPos - tickSliderPos) <= snapThreshold) {
          return tick.value;
        }
      }
      return null;
    },
    [ticks, snapThreshold, valueToSlider],
  );

  const handleRange = (sliderPos: any) => {
    const numSliderPos = Number(sliderPos);

    // Check if we should snap to a tick mark
    const snappedValue = getSnappedValue(numSliderPos);
    if (snappedValue !== null) {
      setActualValue(snappedValue);
      setInputValue(formatDisplayValue(snappedValue));
      props.getValue(snappedValue);
      return;
    }

    const actualVal = sliderToValue(numSliderPos);
    const roundedVal = Math.round(actualVal);
    setActualValue(roundedVal);
    setInputValue(formatDisplayValue(roundedVal));
    props.getValue(roundedVal);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    let value = parseFloat(inputValue);
    if (isNaN(value)) {
      value = actualValue;
    }
    value = Math.max(min, Math.min(max, value));
    value = Math.round(value);
    setActualValue(value);
    setInputValue(formatDisplayValue(value));
    props.getValue(value);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    e.target.select();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  // Calculate slider position (0-100) from actual value
  const sliderPosition = valueToSlider(actualValue);

  // Calculate percentage for fill
  const fillPercentage = fillBeforeBall
    ? ((Number(actualValue) - min) / (max - min)) * 100
    : 0;

  return (
    <Main hasTicks={!!ticks.length}>
      <SliderContainer>
        <Slider
          type="range"
          min={0}
          max={100}
          value={sliderPosition}
          ballColor={ballColor}
          onChange={e => {
            handleRange(e.target.value);
          }}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          hasTicks={!!ticks.length}
          fillPercentage={fillPercentage}
          fillBeforeBall={fillBeforeBall}
          inputHeight={inputHeight}
          noMargin={noMargin}
          ballOpacityDefault={ballOpacityDefault || 0.7}
          cursor={cursor}
        />
        {isDragging && (
          <SliderTooltip style={{ left: `${sliderPosition}%` }}>
            {formatTooltip
              ? formatTooltip(actualValue)
              : `${formatDisplayValue(actualValue)}${unit}`}
          </SliderTooltip>
        )}
        {ticks.length > 0 && (
          <TickMarksOverlay>
            {ticks.map(tick => (
              <TickMarkPositioned
                key={tick.value}
                position={valueToSlider(tick.value)}
              >
                <TheTick />
                <TickLabel>{tick.label}</TickLabel>
              </TickMarkPositioned>
            ))}
          </TickMarksOverlay>
        )}
      </SliderContainer>
      {showInput && (
        <InputContainer>
          <NumericInput
            type="text"
            value={isFocused ? inputValue : `${inputValue}${unit}`}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
          />
        </InputContainer>
      )}
    </Main>
  );
};

export default SliderSelect;

const Main = styled.div<{ hasTicks: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding-bottom: ${props => (props.hasTicks ? '22px' : '0')};
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const SliderTooltip = styled.div`
  position: absolute;
  top: -36px;
  transform: translateX(-50%);
  background: var(--popover);
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 120%;
  padding: 8px 12px;
  border: 1px solid var(--gray-600);
  border-radius: var(--radius);
  box-shadow: var(--elevation-md);
  white-space: nowrap;
  pointer-events: none;
  z-index: 100005;
`;

const Slider = styled.input<{
  ballColor: string;
  hasTicks: boolean;
  fillPercentage: number;
  fillBeforeBall: boolean;
  ballOpacityDefault: number;
  inputHeight?: string;
  noMargin?: boolean;
  cursor?: 'pointer' | 'default';
}>`
  appearance: none;
  -webkit-appearance: none;
  z-index: 2;
  width: 100%;
  height: ${props => props.inputHeight || '1px'};
  background: ${props =>
    `linear-gradient(to right, ${props.ballColor} 0%, ${props.ballColor} ${props.fillPercentage}%, var(--text-muted) ${props.fillPercentage}%, var(--text-muted) 100%)`};
  border-radius: 2px;
  outline: none;
  opacity: ${props => props.ballOpacityDefault};
  transition: opacity var(--transition-normal);
  align-items: center;
  ${props => (props.noMargin ? 'margin: 0;' : 'margin: 2px')}
  cursor: ${props => props.cursor};

  &:hover {
    opacity: 1;
  }

  &::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: ${props => (!props.fillBeforeBall ? '15px' : '0')};
    height: ${props => (!props.fillBeforeBall ? '15px' : '0')};
    border-radius: 50%;
    background: ${props => props.ballColor};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  &:hover::-webkit-slider-thumb {
    transform: scale(1.2);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }

  &:active::-webkit-slider-thumb {
    transform: scale(1.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    background: var(--foreground);
  }

  &::-moz-range-thumb {
    width: ${props => (!props.fillBeforeBall ? '16px' : '0')};
    height: ${props => (!props.fillBeforeBall ? '16px' : '0')};
    border: none;
    border-radius: 50%;
    background: ${props => props.ballColor};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  &:hover::-moz-range-thumb {
    transform: scale(1.2);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }

  &:active::-moz-range-thumb {
    transform: scale(1.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    background: var(--foreground);
  }
`;

const TickMarksOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
`;

const TickMarkPositioned = styled.div<{ position: number }>`
  position: absolute;
  left: ${props => props.position}%;
  top: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TheTick = styled.div`
  width: 1px;
  height: 10px;
  background: var(--text-muted);
`;

const TickLabel = styled.div`
  color: var(--text-muted);
  font-size: 9px;
  white-space: nowrap;
  margin-top: 3px;
  text-align: center;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  flex-shrink: 0;
`;

const NumericInput = styled.input`
  width: 48px;
  padding: 4px 6px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-align: center;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
